import { useState, useEffect } from "react";
import { DoctorSessionTimingSelection } from "./TimeSlots";
import ClinicForm from "./AddNewSelfClinic";
import { Preference, TimeFrame } from "../AddNewDoctor";
import toast from "react-hot-toast";
import { DoctorService } from "../../services/api/doctor.service";
import LocationPicker from "../GoogleMap";

interface AddNewPreferenceProps {
  type: string;
  onSave: (preferenceData: Omit<Preference, "id">) => void;
  editingPreference: Preference | null;
  isMonthly?: boolean;
  onToggleScheduling?: () => void;
}

export const DAYS_ORDER = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

interface AddressDetails {
  fullAddress: string;
  city: string;
  state: string;
  pincode: string;
}

interface NewTimeSlot {
  start_time: string;
  end_time: string;
  slot_duration: number;
}

export const AddNewPreference = ({
  type,
  onSave,
  editingPreference,
  isMonthly,
  onToggleScheduling,
}: AddNewPreferenceProps) => {
  const [clinicDetails, setClinicDetails] = useState({
    id: "",
    name: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    lat: 0,
    long: 0,
    mobile_number: "",
  });

  const [consultationFee, setConsultationFee] = useState<number>(0);
  const [enabledDays, setEnabledDays] = useState<Record<string, boolean>>({});
  const [timeFrames, setTimeFrames] = useState<TimeFrame[]>([]);
  const [address, setAddress] = useState("");
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const [showMap, setShowMap] = useState(false);
  const [addingSlots, setAddingSlots] = useState<
    Record<string, NewTimeSlot | null>
  >({});
  const [clinicListing, setClinicListing] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(1);

  const toggleDay = (day: string) => {
    setEnabledDays((prev) => ({ ...prev, [day]: !prev[day] }));

    if (enabledDays[day]) {
      setTimeFrames((prev) => prev.filter((slot) => slot.day_of_week !== day));
    }
  };

  const handleAddSlotClick = (day: string) => {
    setAddingSlots((prev) => ({
      ...prev,
      [day]: { start_time: "09:00", end_time: "17:00", slot_duration: 15 },
    }));
  };

  const handleTimeSlotChange = (
    slotId: string,
    field: "start_time" | "end_time" | "slot_duration",
    value: string | number,
  ) => {
    setTimeFrames((prev) =>
      prev.map((slot) =>
        slot.id === slotId
          ? {
              ...slot,
              [field]:
                field === "slot_duration" ? Number(value) : String(value),
            }
          : slot,
      ),
    );
  };

  const handleSaveSlot = (day: string) => {
    const slot = addingSlots[day];
    if (!slot) return;

    setTimeFrames((prev) => [
      ...prev,
      {
        id: `L-${Date.now()}`,
        day_of_week: day,
        start_time: slot.start_time,
        end_time: slot.end_time,
        slot_duration: slot.slot_duration,
      },
    ]);

    setAddingSlots((prev) => ({ ...prev, [day]: null }));
  };

  const handleRemoveSlot = (slotId: string) => {
    setTimeFrames((prev) => prev.filter((s) => s.id !== slotId));
  };

  const handleClinicDetailsOnChange = (fieldName: string, value: string) => {
    setClinicDetails((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };
  useEffect(() => {
    setAddress(
      clinicDetails.address +
        " " +
        clinicDetails.city +
        " " +
        clinicDetails.state +
        "  " +
        clinicDetails.pincode,
    );
  }, [
    clinicDetails.address,
    clinicDetails.city,
    clinicDetails.state,
    clinicDetails.pincode,
  ]);
  const handleLocate = async () => {
    setShowMap(true);
  };

  const handleSaveChanges = () => {
    // Validation
    if (type === "Self Clinic") {
      if (
        !clinicDetails.name ||
        !clinicDetails.address ||
        !clinicDetails.city ||
        !clinicDetails.state ||
        !clinicDetails.pincode
      ) {
        toast.error("All clinic details are required for Self Clinic.");
        return;
      }
    } else if (type === "Dandar Clinic") {
      if (!clinicDetails.id || !clinicDetails.name) {
        toast.error("Clinic selection is required for Dandar Clinic.");
        return;
      }
    }

    if (
      type === "Self Clinic" ||
      type === "Dandar Clinic" ||
      type === "Online"
    ) {
      const pinRegex = /^[1-9][0-9]{5}$/;
      if (clinicDetails.pincode && !pinRegex.test(clinicDetails.pincode)) {
        toast.error("Please enter a valid 6-digit pincode.");
        return;
      }
    }

    const isAnyDayEnabled = Object.values(enabledDays).some((day) => day);
    if (!isAnyDayEnabled) {
      toast.error("Please enable at least one day before saving.");
      return;
    }

    for (const day of Object.keys(enabledDays)) {
      if (enabledDays[day]) {
        const daySlots = timeFrames.filter((s) => s.day_of_week === day);
        if (daySlots.length === 0) {
          toast.error(`Please add slots for ${day}.`);
          return;
        }
      }
    }

    for (const slot of timeFrames) {
      if (slot.start_time >= slot.end_time) {
        toast.error(
          `Start time must be less than end time for ${slot.day_of_week}.`,
        );
        return;
      }
    }

    const groupedByDay: Record<string, TimeFrame[]> = {};
    timeFrames.forEach((slot) => {
      if (!groupedByDay[slot.day_of_week]) groupedByDay[slot.day_of_week] = [];
      groupedByDay[slot.day_of_week].push(slot);
    });

    for (const day in groupedByDay) {
      const slots = groupedByDay[day];
      for (let i = 0; i < slots.length; i++) {
        for (let j = i + 1; j < slots.length; j++) {
          const s1 = slots[i];
          const s2 = slots[j];
          if (s1.start_time < s2.end_time && s1.end_time > s2.start_time) {
            toast.error(
              `Overlapping slots detected on ${day}. Please adjust timings.`,
            );
            return;
          }
        }
      }
    }

    if (!consultationFee || consultationFee <= 0) {
      toast.error("Consultation fee is required.");
      return;
    }

    // Prepare preference data
    const preferenceData: Omit<Preference, "id"> = {
      type: type as
        | "Online"
        | "Dandar Clinic"
        | "Self Clinic"
        | "Instent Appointment",
      clinicDetails: { ...clinicDetails },
      consultationFee,
      enabledDays: { ...enabledDays },
      timeFrames: [...timeFrames],
      weekOfMonth:
        isMonthly && (type === "Dandar Clinic" || type === "Self Clinic")
          ? selectedWeek
          : undefined,
    };

    // Call the onSave function passed from parent
    onSave(preferenceData);

    console.log("preferenceData in add new preference", preferenceData);

    // Reset form
    setClinicDetails({
      id: "",
      name: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      lat: 0,
      long: 0,
      mobile_number: "",
    });
    setConsultationFee(0);
    setEnabledDays({});
    setTimeFrames([]);
    setAddingSlots({});
    setSelectedWeek(1);
  };

  // Load data when editing
  useEffect(() => {
    if (editingPreference) {
      setClinicDetails({
        ...editingPreference.clinicDetails,
        lat: editingPreference.clinicDetails.lat || 0,
        long: editingPreference.clinicDetails.long || 0,
        mobile_number: editingPreference.clinicDetails.mobile_number || "",
      });
      setConsultationFee(editingPreference.consultationFee);
      setEnabledDays({ ...editingPreference.enabledDays });
      setTimeFrames([...editingPreference.timeFrames]);
      if (editingPreference.weekOfMonth) {
        setSelectedWeek(editingPreference.weekOfMonth);
      }
    }
  }, [editingPreference]);

  const loadClinicListing = async () => {
    try {
      const clinicRes = await DoctorService.fetchClinicListing(1, 1000, "");

      if (clinicRes.success) {
        setClinicListing(clinicRes.data.dataList || []);
      } else {
        setClinicListing([]);
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    if (type === "Dandar Clinic") {
      loadClinicListing();
    }
  }, [type]);

  const handleLocationSelect = (
    lat: string,
    lng: string,
    addressDetails: AddressDetails,
  ) => {
    setClinicDetails((prev) => ({
      ...prev,
      lat: parseFloat(lat),
      long: parseFloat(lng),
      address: addressDetails.fullAddress,
      city: addressDetails.city,
      state: addressDetails.state,
      pincode: addressDetails.pincode,
    }));
  };

   const handleApplySameAs = () => {
      // Find the first day with slots based on DAYS_ORDER
      let sourceDay = "";
      let sourceSlots: TimeFrame[] = [];
  
      for (const day of DAYS_ORDER) {
        const slots = timeFrames.filter((s) => s.day_of_week === day);
        if (slots.length > 0) {
          sourceDay = day;
          sourceSlots = slots;
          break;
        }
      }
  
      if (!sourceDay) {
        toast.error("Please add slots to at least one day first.");
        return;
      }
  
      // Create new slots for all other days (enabling them if disabled)
      // We keep existing slots of the source day and overwrite others
      const framesToKeep = timeFrames.filter((s) => s.day_of_week === sourceDay);
      const newTimeFrames = [...framesToKeep];
      const newEnabledDays = { ...enabledDays };
  
      for (const day of DAYS_ORDER) {
        if (day === sourceDay) continue;
  
        // Enable the day
        newEnabledDays[day] = true;
  
        // Copy slots
        sourceSlots.forEach((slot) => {
          newTimeFrames.push({
            ...slot,
            id: `L-${Date.now()}-${day}-${Math.random()}`, // Unique ID
            day_of_week: day,
          });
        });
      }
  
      setEnabledDays(newEnabledDays);
      setTimeFrames(newTimeFrames);
      toast.success(`Copied slots from ${sourceDay} to all days.`);
    };
  

  return (
    <div className="w-[100%] mx-auto relative">
      <div className="overflow-hidden overflow-y-auto hiddenScrollbar relative">
        {/* Scheduling Toggle */}
        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg mb-4 border border-gray-200">
          <div>
            <h4 className="font-medium text-gray-800 text-sm">
              Scheduling Type
            </h4>
            <p className="text-xs text-gray-500">
              Current:{" "}
              <span className="font-semibold text-emerald-600">
                {isMonthly ? "Monthly" : "Weekly"}
              </span>
            </p>
          </div>

          {onToggleScheduling && (
            <button
              onClick={onToggleScheduling}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                isMonthly ? "bg-emerald-600" : "bg-gray-200"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isMonthly ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          )}
        </div>

        {isMonthly && (type === "Dandar Clinic" || type === "Self Clinic") && (
          <div className="mb-4">
            <label className="text-sm font-medium text-[#3F5260] mb-2 block">
              Select Week of Month
            </label>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((week) => (
                <button
                  key={week}
                  onClick={() => setSelectedWeek(week)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border transition-all ${
                    selectedWeek === week
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  Week {week}
                </button>
              ))}
            </div>
          </div>
        )}

        {(type === "Self Clinic" || type === "Online") && (
          <>
            <ClinicForm
              formData={clinicDetails}
              onChange={(fieldName, value) =>
                handleClinicDetailsOnChange(fieldName, value)
              }
            />
            <button
              type="button"
              onClick={handleLocate}
              className="px-4 py-2 my-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              🗺️ Locate on Map
            </button>
          </>
        )}

        {type === "Dandar Clinic" && (
          <div className="flex flex-col gap-2 items-start w-full mb-4">
            <label
              htmlFor="clinicType"
              className="text-sm font-medium text-[#3F5260]"
            >
              Select Dandar Clinic
            </label>
            <select
              id="clinicType"
              name="clinicType"
              value={clinicDetails.id}
              onChange={(e) => {
                const selected: any = clinicListing.find(
                  (c: any) => c.id === e.target.value,
                );
                if (selected) {
                  setClinicDetails({
                    id: selected.id,
                    name: selected.name,
                    address: selected.address,
                    city: selected.city,
                    state: selected.state,
                    pincode: selected.pincode,
                    lat: selected.lat,
                    long: selected.long,
                    mobile_number:
                      selected.mobileNumber || selected.mobile_number || "",
                  });
                }
              }}
              className="border border-[#D0D5DE] w-full text-black font-light text-sm p-1 pr-10 outline-none rounded"
            >
              <option value="">-- Select a Clinic --</option>
              {clinicListing.length > 0 ? (
                clinicListing.map((clinic: any) => (
                  <option key={clinic.id} value={clinic.id}>
                    {clinic.name}
                  </option>
                ))
              ) : (
                <option disabled>No clinics found</option>
              )}
            </select>

            {clinicDetails.id && (
              <div className="w-full mt-2">
                <label className="block text-sm font-medium text-[#3F5260] mb-1">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  maxLength={10}
                  placeholder="Enter clinic mobile number"
                  value={clinicDetails.mobile_number || ""}
                  onChange={(e) =>
                    handleClinicDetailsOnChange("mobile_number", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-[#D0D5DE] rounded focus:outline-none focus:ring-1 focus:ring-green-500 text-sm"
                />
              </div>
            )}
          </div>
        )}

        <DoctorSessionTimingSelection
          consultationFee={consultationFee}
          enabledDays={enabledDays}
          timeFrames={timeFrames}
          addingSlots={addingSlots}
          setConsultationFee={setConsultationFee}
          setAddingSlots={setAddingSlots}
          toggleDay={toggleDay}
          handleAddSlotClick={handleAddSlotClick}
          handleSaveSlot={handleSaveSlot}
          handleRemoveSlot={handleRemoveSlot}
          handleTimeSlotChange={handleTimeSlotChange}
          handleApplySameAs={handleApplySameAs}
          
        />
      </div>

      <div className="w-full p-2 mt-4">
        <button
          onClick={handleSaveChanges}
          className="w-full py-2 bg-green-600 text-white rounded border border-green-700 hover:bg-green-700 text-sm font-medium flex items-center justify-center gap-1"
        >
          {editingPreference ? "Update Preference" : "Save Preference"}
        </button>
      </div>

      {showMap && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <LocationPicker
            location={location}
            setLocation={setLocation}
            address={address}
            setShowMap={setShowMap}
            onLocationSelect={handleLocationSelect}
          />
        </div>
      )}
    </div>
  );
};
